// Polaris strategy thunks — thin wrappers over the real Polaris API.
import {createAsyncThunk} from "@reduxjs/toolkit";
import polarisApi from "api/polarisApi";
import {
	TCalibrationVote,
	TConfirmablePhase,
	TPolarisPhaseName,
	TRoleFieldPatchRequest,
	TSaveToProjectRequest,
	TVoteResponse,
} from "api/polarisApi/types";
import type {TRootState} from "types";
import {loadFootprint, refreshOnFootprintConflict} from "./footprintThunks";
import {calibrationSampleTimeoutError} from "./calibrationGeneration";
import {ensureOk, searchIdOf} from "./thunkUtils";
import {pollSearchStatus} from "./searchRunThunks";
import {loadCriteria} from "./criteriaThunks";
export * from "./criteriaThunks";

// Phase 5 search-run thunks live in ./searchRunThunks; re-export so existing
// imports from this module keep working.
export * from "./searchRunThunks";

// Recent-searches thunk lives in ./recentSearchesThunks; re-export it too.
export * from "./recentSearchesThunks";

// Phase 2 footprint thunks live in ./footprintThunks; re-export them too.
export * from "./footprintThunks";

export const createSearch = createAsyncThunk("polaris/createSearch", async (brief: string) => {
	return ensureOk(await polarisApi.createSearch(brief), "Could not create the search");
});

export const sendMessage = createAsyncThunk(
	"polaris/sendMessage",
	async (text: string, {getState}) => {
		const id = searchIdOf(getState);

		return ensureOk(await polarisApi.postMessage(id, text), "Could not send the message");
	},
);

// ---- Per-phase loads ----

export const loadRoleBasics = createAsyncThunk(
	"polaris/loadRoleBasics",
	async (_: void, {getState}) =>
		ensureOk(
			await polarisApi.getRoleBasics(searchIdOf(getState)),
			"Could not load role basics",
		),
);

export const loadCalibration = createAsyncThunk(
	"polaris/loadCalibration",
	async (_: void, {getState}) =>
		ensureOk(
			await polarisApi.getCalibration(searchIdOf(getState)),
			"Could not load the calibration sample",
		),
);

export const loadProjects = createAsyncThunk("polaris/loadProjects", async () =>
	ensureOk(await polarisApi.getProjects(), "Could not load projects"),
);

export const loadHandoffSummary = createAsyncThunk(
	"polaris/loadHandoffSummary",
	async (_: void, {getState}) =>
		ensureOk(
			await polarisApi.getHandoffSummary(searchIdOf(getState)),
			"Could not load the handoff summary",
		),
);

/**
 * Dispatches the data loads a given phase needs. Inner loads are unwrapped so
 * a failure rejects loadPhaseData (the workspace then shows an error + retry
 * instead of a perpetual skeleton). The `condition` drops a duplicate load for
 * a phase already in flight — confirmPhase and the SSE phase event can both
 * request the same phase at once.
 */
export const loadPhaseData = createAsyncThunk(
	"polaris/loadPhaseData",
	async (phase: TPolarisPhaseName, {dispatch}) => {
		switch (phase) {
			case "role_basics":
				await dispatch(loadRoleBasics()).unwrap();
				break;
			case "footprint":
				await dispatch(loadFootprint()).unwrap();
				break;
			case "calibration":
				await dispatch(loadCalibration()).unwrap();
				break;
			case "criteria":
				await dispatch(loadCriteria()).unwrap();
				break;
			case "search":
				await dispatch(pollSearchStatus()).unwrap();
				break;
			case "handoff":
				await Promise.all([
					dispatch(loadHandoffSummary()).unwrap(),
					dispatch(loadProjects()).unwrap(),
				]);
				break;
			default:
				break;
		}

		return phase;
	},
	{
		condition: (phase, {getState}) =>
			(getState() as TRootState).polaris.loading_phase !== phase,
	},
);

/**
 * Page-load bootstrap: transcript (chat history + current phase) followed by
 * the active phase's data.
 */
export const bootstrapSearch = createAsyncThunk(
	"polaris/bootstrapSearch",
	async (searchId: string, {dispatch}) => {
		const transcript = ensureOk(
			await polarisApi.getTranscript(searchId, 0),
			"Could not load this search",
		);

		await dispatch(loadPhaseData(transcript.current_phase));

		return {search_id: searchId, ...transcript};
	},
);

// ---- Phase 1 ----

export const updateRoleField = createAsyncThunk(
	"polaris/updateRoleField",
	async (arg: {fieldKey: string; body: TRoleFieldPatchRequest}, {getState}) =>
		ensureOk(
			await polarisApi.patchRoleField(searchIdOf(getState), arg.fieldKey, arg.body),
			"Could not update the field",
		),
);

// ---- Phase 3 ----

export const castVote = createAsyncThunk(
	"polaris/castVote",
	async (arg: {candidateRef: string; vote: TCalibrationVote; rationale?: string}, {getState}) =>
		// doPost returns an untyped AxiosResponse — pin the payload type here
		ensureOk<TVoteResponse>(
			await polarisApi.postVote(searchIdOf(getState), {
				candidate_ref: arg.candidateRef,
				vote: arg.vote,
				rationale: arg.rationale || null,
			}),
			"Could not record the vote",
		),
);

const RESAMPLE_POLL_INTERVAL_MS = 3000;
const RESAMPLE_POLL_TIMEOUT_MS = 30000;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const resample = createAsyncThunk(
	"polaris/resample",
	async (_: void, {getState, dispatch}) => {
		const result = ensureOk(
			await polarisApi.postResample(searchIdOf(getState)),
			"Could not resample",
		);

		// Resample returns ids only — fetch the fresh sample. The rebuild can take
		// ~10-25s server-side, during which GET /calibration returns no candidates;
		// poll until the round is ready. Past the timeout (or once the backend
		// reports the build failed) reject, so the card can offer Try again.
		const deadline = Date.now() + RESAMPLE_POLL_TIMEOUT_MS;
		let fresh = await dispatch(loadCalibration()).unwrap();

		while (fresh.candidates.length === 0) {
			if (fresh.generation?.status === "failed" || Date.now() >= deadline) {
				throw calibrationSampleTimeoutError();
			}

			await wait(RESAMPLE_POLL_INTERVAL_MS);
			fresh = await dispatch(loadCalibration()).unwrap();
		}

		return result;
	},
);

// Try again after the first sample (or a resample) did not arrive. Returns the
// GET /calibration payload; the fallback poll takes over from there.
export const retryCalibration = createAsyncThunk(
	"polaris/retryCalibration",
	async (_: void, {getState}) =>
		ensureOk(
			await polarisApi.retryCalibration(searchIdOf(getState)),
			"Could not retry the calibration sample",
		),
);

// ---- Phase transitions ----

export const confirmPhase = createAsyncThunk(
	"polaris/confirmPhase",
	async (phase: TConfirmablePhase, {getState, dispatch}) => {
		// The footprint confirm is refused (409 footprint_generating /
		// footprint_failed) until the map is ready — refresh rather than error.
		const result = await refreshOnFootprintConflict(dispatch, async () =>
			ensureOk(
				await polarisApi.confirmPhase(searchIdOf(getState), phase),
				"Could not advance the phase",
			),
		);

		// The footprint confirm returns {phase} instead of the spec'd
		// {current_phase} — normalise so the UI never sees an empty phase.
		const nextPhase = result.current_phase ?? result.phase;

		if (!nextPhase) throw new Error("Phase confirm returned no phase");

		await dispatch(loadPhaseData(nextPhase));

		return {...result, current_phase: nextPhase};
	},
);

// ---- Phase 6 ----

export const saveToProject = createAsyncThunk(
	"polaris/saveToProject",
	async (body: TSaveToProjectRequest, {getState}) =>
		ensureOk(
			await polarisApi.saveToProject(searchIdOf(getState), body),
			"Could not save to the project",
		),
);
